import { Button, Card, Col, Row, Space, Tag, Typography } from 'antd'
import { Link } from 'react-router-dom'

import { Mascot } from './mascot'
import { Section } from './sections'

/**
 * The remaining sections: pipeline gate, cost, where we sit beside the rest of
 * the stack, security, and the closing call to action.
 *
 * The security section answers the question profiling a production process
 * raises immediately, and answers only what is true. Nothing here claims a
 * certification, self-hosting or a role model, because we have none of those.
 */

/** The CI gate. Real, shipped, and honest about what it cannot do. */
export function PipelineGate() {
  return (
    <Section
      title="Check a release before it reaches everybody"
      lead="Deploy a candidate to staging or a canary, let it run long enough to be measured, and ask whether it costs more than the build it replaces. Exit code 1 if it does."
    >
      <Row gutter={[32, 24]}>
        <Col xs={24} md={13}>
          <div
            style={{
              background: '#0d1218',
              borderRadius: 10,
              padding: 20,
              overflowX: 'auto',
              color: '#d6e4ff',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 13,
              lineHeight: 1.7,
            }}
          >
            <pre style={{ margin: 0 }}>{`- name: Performance check
  run: |
    perfcheck \\
      -service checkout-api \\
      -environment staging \\
      -candidate "\${{ github.sha }}" \\
      -wait 10m
  env:
    PECGOPROF_API_KEY: \${{ secrets.PECGOPROF_API_KEY }}`}</pre>
          </div>
        </Col>

        <Col xs={24} md={11}>
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            <div>
              <Tag color="green">passed</Tag>
              <Typography.Text type="secondary">nothing took materially more</Typography.Text>
            </div>
            <div>
              <Tag color="red">failed</Tag>
              <Typography.Text type="secondary">a function gained enough to matter</Typography.Text>
            </div>
            <div>
              <Tag>inconclusive</Tag>
              <Typography.Text type="secondary">not enough data yet — its own exit code</Typography.Text>
            </div>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Profiling data arrives after a build runs, so this cannot block a merge and does not
              pretend to. A gate that answered "passed" when it had nothing to compare would
              report success for every build checked too early — and look like it was working
              right up until the release it was meant to catch.
            </Typography.Paragraph>
          </Space>
        </Col>
      </Row>
    </Section>
  )
}

/** Cost, without inventing a percentage. */
export function CostAngle() {
  return (
    <Section title="Slower code is a bigger bill" dark mascot="running">
      <Row gutter={[32, 24]} align="middle">
        <Col xs={24} md={14}>
          <Typography.Paragraph style={{ color: 'rgba(255,255,255,0.75)', fontSize: 17 }}>
            A few points of CPU on one service is nothing. The same few points across every replica
            of that service, for a month, is an instance size. Allocation churn is the same story
            told through the garbage collector.
          </Typography.Paragraph>
          <Typography.Paragraph style={{ color: 'rgba(255,255,255,0.75)', fontSize: 17, marginBottom: 0 }}>
            PecGoProf shows which function the compute is going to, so the answer to a rising bill
            can be a change rather than a bigger instance.
          </Typography.Paragraph>
        </Col>
        <Col xs={24} md={10}>
          <Card size="small" style={{ background: '#12181f', borderColor: 'rgba(255,255,255,0.12)' }}>
            <Typography.Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
              We do not publish a savings figure. We have not run your workload, and a number we
              made up would be the least trustworthy thing on this page.
            </Typography.Text>
          </Card>
        </Col>
      </Row>
    </Section>
  )
}

/** Beside the stack, not instead of it. */
export function AlongsideYourStack() {
  return (
    <Section
      title="Keep the monitoring you already have"
      lead="PecGoProf does one thing: Go performance analysis. It does not collect your metrics, store your logs or trace your requests, and it is not trying to."
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} md={8}>
          <Card size="small" style={{ height: '100%' }}>
            <Typography.Text strong>Metrics</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ marginTop: 6, marginBottom: 0 }}>
              Prometheus, Grafana, Datadog tell you <em>that</em> something changed.
            </Typography.Paragraph>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" style={{ height: '100%' }}>
            <Typography.Text strong>Traces</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ marginTop: 6, marginBottom: 0 }}>
              Tell you which request or service the time went to.
            </Typography.Paragraph>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" style={{ height: '100%', borderColor: '#1668dc' }}>
            <Typography.Text strong>PecGoProf</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ marginTop: 6, marginBottom: 0 }}>
              Tells you which <em>function</em> — and which build changed it.
            </Typography.Paragraph>
          </Card>
        </Col>
      </Row>
    </Section>
  )
}

/** What we do with the data. Only true answers. */
export function Trust() {
  const facts: [string, string][] = [
    ['What is collected', 'pprof profiles: function names, call paths and measurements. Plus what the SDK reports about the deploy — service, environment, instance, build.'],
    ['Your source code', 'Never uploaded. A profile carries the names of functions, not their bodies.'],
    ['Where it is kept', 'Amazon S3 and PostgreSQL in AWS us-east-1, both encrypted at rest. Traffic is encrypted in transit.'],
    ['How long', '7 to 180 days depending on the plan. A daily job deletes what has passed the window, from storage and database together.'],
    ['Deleting it', 'Delete a profile from the application, or close the account and everything goes with it.'],
    ['Training', 'The contents of your profiles are never used to train models. Automated analysis runs on hardware we operate.'],
  ]

  return (
    <Section
      title="Profiling production raises a question"
      lead="It should. Here is what actually happens to the data — and the privacy policy says the same thing in longer form."
    >
      <Row gutter={[24, 24]}>
        {facts.map(([q, a]) => (
          <Col xs={24} md={12} key={q}>
            <Typography.Text strong>{q}</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
              {a}
            </Typography.Paragraph>
          </Col>
        ))}
        <Col span={24}>
          <Link to="/privacy">Read the privacy policy →</Link>
        </Col>
      </Row>
    </Section>
  )
}

/** The last ask. */
export function ClosingCTA() {
  return (
    <div style={{ background: '#1668dc', color: '#fff', padding: '64px 24px', textAlign: 'center' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <Mascot pose="laptop" size={116} />
        <Typography.Title level={2} style={{ color: '#fff', marginTop: 8 }}>
          Stop finding out from the bill
        </Typography.Title>
        <Typography.Paragraph style={{ color: 'rgba(255,255,255,0.85)', fontSize: 17 }}>
          Five lines in your service, and the next deploy that costs more tells you so.
        </Typography.Paragraph>
        <Space size={12} wrap style={{ justifyContent: 'center' }}>
          <Link to="/login?tab=register&plan=developer">
            <Button size="large">Start your 14-day trial</Button>
          </Link>
          <Link to="/demo">
            <Button size="large" ghost>
              Look around the demo first
            </Button>
          </Link>
        </Space>
      </div>
    </div>
  )
}
